<template>
    <div class="auth-page">
        <div class="auth-page-container">
            <div v-if="whitelabel && whitelabel.data" class="auth-header" :style="authHeaderStyle">
                <!-- <img src="@/assets/logo-new.svg" alt="Logo" class="logo-auth" /> -->
                <img :src="logoSrc" :alt="logoAlt" :class="logoClass" />
                <h2>
                    {{
                        isLogin
                            ? "Simplifique suas compras e feche os melhores negócios."
                            : "Tenha tudo à mão e gerencie seus produtos com facilidade."
                    }}
                </h2>
            </div>

            <div class="auth-container">
                <!-- <img src="@/assets/logo-mobile.svg" class="logo-mobile" /> -->
                <img :src="logoSrcMobile" :alt="logoAlt" :class="logoClassMobile" />
                <div class="auth-form">
                    <h3>{{ isLogin ? "Entre no Club" : "Crie sua conta" }}</h3>

                    <a-alert v-if="error_msg" :message="error_msg" type="error" show-icon class="alert-message" />

                    <form v-if="isLogin" @submit.prevent="efetuarLogin" class="login-form">
                        <StandardSimpleInput
                            label="Usuário"
                            v-model="login"
                            type="text"
                            placeholder="Digite seu usuário"
                            :error="fieldErrors.login"
                            @clear-error="clearFieldError('login')"
                            class="form-input"
                            @input="clearError"
                        />
                        <StandardSimpleInput
                            label="Senha"
                            v-model="password"
                            type="password"
                            placeholder="Digite sua senha"
                            :error="fieldErrors.password"
                            @clear-error="clearFieldError('password')"
                            class="form-input"
                            @input="clearError"
                        />
                        <div class="form-options">
                            <label class="checkbox-label">
                                <input type="checkbox" v-model="saveLogin" />
                                Lembrar deste dispositivo
                            </label>
                            <a href="https://admin.clubdacotacao.com.br/reset-password">Esqueceu sua senha?</a>
                        </div>

                        <AuthSubmitButton
                            :loading="loading"
                            :default-text="'Entrar'"
                            :loading-text="'Entrando...'"
                            class="submit-button"
                            html-type="submit"
                            @click="efetuarLogin"
                        />
                    </form>

                    <form v-else @submit.prevent="efetuarCadastro" class="signup-form">
                        <StandardSimpleInput
                            label="Nome"
                            v-model="name"
                            type="text"
                            placeholder="Digite seu nome"
                            :error="fieldErrors.name"
                            @clear-error="clearFieldError('name')"
                            class="form-input"
                            @input="clearError"
                        />

                        <StandardSimpleInput
                            label="Email"
                            v-model="email"
                            type="text"
                            placeholder="Digite seu email"
                            :error="fieldErrors.email"
                            @clear-error="clearFieldError('email')"
                            class="form-input"
                            @input="clearError"
                        />

                        <div class="password-section">
                            <div class="password-label-container">
                                <label class="password-label">Senha</label>
                                <span class="password-strength-text" :class="senha ? strengthClass : ''">
                                    {{ senha ? strengthText : "Força da senha" }}
                                </span>
                            </div>

                            <StandardSimpleInput
                                :error="fieldErrors.senha"
                                type="password"
                                v-model="senha"
                                @input="onPasswordInput"
                                @clear-error="clearFieldError('senha')"
                                placeholder="Digite sua senha"
                                class="form-input password-input"
                            />

                            <div class="password-strength">
                                <div v-for="n in 4" :key="n" :class="['bar', strengthLevel >= n ? strengthClass : '']"></div>
                            </div>

                            <ul class="password-rules">
                                <li :class="{ valid: validations.minLength }">
                                    Mínimo de 8 caracteres
                                </li>
                                <li :class="{ valid: validations.uppercase }">
                                    1 letra maiúscula (A-Z)
                                </li>
                                <li :class="{ valid: validations.lowercase }">
                                    1 letra minúscula (a-z)
                                </li>
                                <li :class="{ valid: validations.number }">1 número (0-9)</li>
                                <li :class="{ valid: validations.special }">
                                    1 caractere especial (ex: @, #, $, &)
                                </li>
                            </ul>
                        </div>

                        <StandardSimpleInput
                            label="Confirme sua senha"
                            :error="fieldErrors.confirmPassword"
                            type="password"
                            v-model="confirmPassword"
                            @input="validatePasswordMatch"
                            @clear-error="clearFieldError('confirmPassword')"
                            placeholder="Digite sua senha novamente"
                            class="form-input"
                        />
                        <div class="form-options" style="margin-top: 10px">
                            <label class="checkbox-label">
                                <input type="checkbox" v-model="acceptTerms" />
                                <div class="terms-text">
                                    Ao criar uma conta no Club, você está de acordo com nossa
                                    <span @click="show = true">Política de privacidade</span> e
                                    <span @click="show = true">Termos de Uso</span>
                                </div>
                            </label>
                        </div>
                        <div class="submit-button-container">
                            <AuthSubmitButton
                                :loading="loading"
                                :default-text="'Criar Conta'"
                                :loading-text="'Criando conta...'"
                                type="submit"
                                class="submit-button"
                            />
                        </div>
                    </form>

                    <span v-if="isLogin">
                        Não está registrado ainda?
                        <a href="https://novo.fornecedor.clubdacotacao.com.br/requisicao/fornecedor">Crie uma conta</a>
                    </span>
                    <span v-else style="margin-bottom: 10px">
                        Possui uma conta?
                        <router-link to="/login">Faça o Login</router-link>
                    </span>
                </div>
                <div class="footer">
                    <p>Club da Cotação® | Todos os direitos reservados</p>
                    <button @click="show = true" v-if="isLogin">Política de privacidade</button>
                </div>
            </div>
        </div>
        <PrivacyModal v-if="show" :value="show" @input="show = $event" />
    </div>
</template>

<script>
import { clear_features } from "@/helpers/features";
import * as CONSTANTS from "@/constants/constants";
import StorageService from "@/services/TokenService";
import VarejoFacilService from "@/services/VarejoFacilService";
import CredentialService from "@/services/v2/credentials.service";
import { gsap } from "gsap";
import { loaderMixin } from "@/mixins/sweet-loader.mixin";
import ErrorHandlerService from "@/services/ErrorHandlerService";
import ClientServiceV3 from "@/services/v3/auth.service";
import { mapState } from "vuex";
import BomControleService from "@/services/BomControleService";
import AuthSubmitButton from "@/components/ui/buttons/submitButton.component.vue";
import StandardSimpleInput from "@/components/ui/inputs/inputSimple.component.vue";
import PrivacyModal from "../../modals/privacy-modal/privacyModal.vue";
import AuthService from "@/services/v1/AuthService.js";

export default {
  mixins: [loaderMixin],
  components: { StandardSimpleInput, PrivacyModal, AuthSubmitButton },
  data() {
    return {
      login: "",
      senha: "",
      loading: false,
      error_msg: "",
      success_msg: null,
      storage_svc: new StorageService(),
      vf_svc: new VarejoFacilService(),
      cred_svc: new CredentialService(),
      clientServiceV3: new ClientServiceV3(),
      bomControle: new BomControleService(),
      // Dados comuns
      password: "",
      success_msg: null,
      show: false,
      saveLogin: false,
      // Dados  do cadastro
      name: "",
      email: "",
      confirmPassword: "",
      acceptTerms: false,
      showPasswordRules: false,
      fieldErrors: {
          name: "",
          password: "",
          email: "",
          confirmPassword: "",
          login: "",
      },
      validations: {
          minLength: false,
          uppercase: false,
          lowercase: false,
          number: false,
          special: false,
      },
      svcV1: new AuthService(),
    };
  },
  methods: {
    handleParagraphClick() {
      console.log(this.whitelabel.data);
    },
    open_send_pass_form() {
      let selector = "#send-password";
      let login_selector = "#login";
      gsap.fromTo(
        login,
        { opacity: 0, x: "100%" },
        { display: "none", x: "-50%", duration: 0, ease: "back.out(1.7)" }
      );
      gsap.fromTo(
        selector,
        { opacity: 0, x: "50%" },
        {
          display: "block",
          opacity: 1,
          x: "0%",
          duration: 1,
          ease: "back.out(1.7)",
        }
      );
    },
    open_login_form() {
      let selector = "#send-password";
      let login_selector = "#login";
      gsap.fromTo(
        selector,
        { opacity: 0, x: "100%" },
        { display: "none", x: "-50%", duration: 0, ease: "back.out(1.7)" }
      );
      gsap.fromTo(
        login_selector,
        { opacity: 0, x: "-20%" },
        {
          display: "block",
          opacity: 1,
          x: "0%",
          duration: 1,
          ease: "back.out(1.1)",
        }
      );
    },
    send_password() {
      this.loading = true;
      this.success_msg = null;
      this.error_msg = null;
      this.cred_svc
        .send_password(this.login)
        .then(() => {
          this.loading = false;
          this.success_msg =
            "Senha enviada com sucesso! Verifique também na sua caixa de spam";
        })
        .catch((e) => {
          this.error_msg =
            "Não conseguimos processar sua requisição, verifique novamente sua credencial informada!";
          this.loading = false;
          ErrorHandlerService.handle(e, this.$store);
        });
    },
    check_home_page_url(settings) {
      if (settings) {
        let home = settings.find((s) => s.key == "DEFAULT_HOME_PAGE");
        if (home && home.value) {
          return home.value;
        }
      }
      return false;
    },
    sendHome(data) {
        let isMobile = window.innerWidth < 691;
      let home = this.check_home_page_url(data.usuario.settings);
      if (home) {
        if (home == "/ofertas") this.$router.push(home + "?login=true");
        else if(isMobile){
            this.$router.push("/inicio");
        }else this.$router.push(home);
      } else {
        if (isMobile) {
          this.$router.push("/inicio");
        } else {
          this.$router.push("/cliente/monitores/cotacoes");
        }
      }
      this.$forceUpdate();
    },
    // efetuarLogin() {
    //   if (/\S/.test(this.login) && /\S/.test(this.senha)) {
    //     this.success_msg = null;
    //     this.error_msg = null;
    //     this.loading = true;
    //     axios
    //       .post(`${CONSTANTS.API_V3_URL}/auth`, {
    //         usu_login: this.login,
    //         senha: this.senha,
    //       })
    //       .then(async ({ data }) => {
    //         this.$router.push("/?token=" + data.token);
    //         // this.loading = false;
    //         // this.storage_svc.clear();
    //         // this.storage_svc.put("token", data.token);
    //         // await this.check_situation();
    //         // this.storage_svc.put(
    //         //   "cli_tipoPedido",
    //         //   data.usuario.cliente.cli_tipoPedido
    //         // );
    //         // this.storage_svc.put("grupo", data.usuario.cliente.cli_nome_grupo);
    //         // this.storage_svc.put("cli_id", data.usuario.cli_id);
    //         // this.storage_svc.put("tpc_id", data.usuario.cliente.tpc_id);
    //         // this.storage_svc.put("user_name", data.usuario.usu_nome);
    //         // this.storage_svc.put("AlertCount", 0);
    //         // this.storage_svc.put(
    //         //   "request_type",
    //         //   data.usuario.cliente.cli_tipoPedido
    //         // );
    //         // this.storage_svc.put(
    //         //   "user_settings",
    //         //   JSON.stringify(data.usuario.settings)
    //         // );
    //         // this.storage_svc.put(
    //         //   "attendance",
    //         //   JSON.stringify(data.usuario.attendance)
    //         // );
    //         // this.storage_svc.put(
    //         //   "store_id",
    //         //   JSON.stringify(data.usuario.usu_idLoja)
    //         // );
    //         // this.storage_svc.put("usu_id", JSON.stringify(data.usuario.usu_id));
    //         // this.storage_svc.put(
    //         //   "closing",
    //         //   data.usuario.cliente.cli_metodo_fechamento
    //         // );
    //         // this.storage_svc.put("showOffers", !data.usuario.usu_idLoja);
    //         // this.storage_svc.put(
    //         //   "cli_utilizaPrecoBase",
    //         //   data.usuario.cliente.cli_utilizaPrecoBase
    //         // );
    //         // if (data.usuario.usuarioPlano.length > 0) {
    //         //   this.storage_svc.put("plan", data.usuario.usuarioPlano[0].status);
    //         // } else {
    //         //   this.storage_svc.put("plan", "0");
    //         // }
    //         // localStorage.setItem("cli_vindi", data.usuario.cliente.cli_vindi);
    //         // this.vf_svc.setup_alert(data.usuario.settings);

    //         // if (data.usuario.varejo_facil_pass)
    //         //   this.storage_svc.put(
    //         //     "has_vf_integration",
    //         //     data.usuario.varejo_facil_pass
    //         //   );
    //         // this.$store.dispatch("user/load_user");
    //         // // this.$store.dispatch('user/load_perms')
    //         // if (data.usuario.cliente.cli_vindi == 1) {
    //         //   this.clientServiceV3.getContractsVindi().then((response) => {
    //         //     this.storage_svc.put(
    //         //       "vindiAccount",
    //         //       JSON.stringify(response.data.custumers.customers[0])
    //         //     );
    //         //     this.storage_svc.put(
    //         //       "contracts",
    //         //       JSON.stringify(response.data.plan.subscriptions)
    //         //     );
    //         //     this.storage_svc.put(
    //         //       "contractsBills",
    //         //       JSON.stringify(response.data.bills.bills)
    //         //     );
    //         //   });
    //         //   this.sendHome(data);
    //         // } else {
    //         //   this.clientServiceV3.getContracts().then((response) => {
    //         //     this.storage_svc.put(
    //         //       "contracts",
    //         //       JSON.stringify(response.data)
    //         //     );
    //         //   });
    //         //   this.sendHome(data);
    //         // }
    //       })
    //       .catch((error) => {
    //         this.loading = false;
    //         if (error.response) {
    //           this.error_msg = error.response.data.mensagem;
    //           this.snack(this.error_msg, "error");
    //         } else {
    //           this.snack("Usuário ou senha inválido", "error");
    //         }
    //         throw error;
    //       });
    //   }
    // },
    async check_situation() {
      try {
        const data = await this.bomControle.checkSituation();
        const hasBoleto = !!data.boleto_url;
        if (data.days_diff && data.days_diff> 0) {
          this.storage_svc.put('situation', data.days_diff >= 10 ? "blocked" : "warning");
          this.storage_svc.put('message', data.mensagem);
          this.storage_svc.put('boleto_url', hasBoleto ? data.boleto_url : "");
          this.storage_svc.put('has_boleto', hasBoleto);
        }else{
          this.storage_svc.put('situation', "ok");
          this.storage_svc.put('message', "");
          this.storage_svc.put('boleto_url', "");
          this.storage_svc.put('has_boleto', false);
        }
      } catch (error) {
        console.error("Erro ao checar situação:", error);
      }
    },
    openModalPrivacy() {
        this.show = true;
    },

    closeModalPrivacy() {
        this.show = false;
    },

    clearError() {
        if (this.error_msg) {
            this.error_msg = "";
        }
        Object.keys(this.fieldErrors).forEach((key) => {
            this.fieldErrors[key] = "";
        });
    },

    clearFieldError(fieldName) {
        if (this.fieldErrors[fieldName]) {
            this.fieldErrors[fieldName] = "";
        }
        this.clearError();
    },
    efetuarLogin() {
        this.verifyInput();
        if (/\S/.test(this.login) && /\S/.test(this.password)) {
            this.loading = true;
            this.error_msg = "";

            this.clientServiceV3
                .authenticate({
                    usu_login: this.login,
                    senha: this.password,
                })
                .then(({ data }) => {
                    this.loading = false;

                    if (this.saveLogin) {
                        localStorage.setItem("LoginInfo", JSON.stringify({ user: this.login, password: this.password }));
                    } else {
                        localStorage.removeItem("LoginInfo");
                    }
                    this.redirect(data);
                })
                .catch((error) => {
                    this.loading = false;
                    this.showLoading = false;
                    this.error_msg = error.response?.data?.errors?.[0] || "Erro ao fazer login. Tente novamente.";
                });
        }
    },

    verifyInput() {
        this.fieldErrors.login = !this.login ? "Usuário é obrigatório" : "";
        this.fieldErrors.password = !this.password ? "Senha é obrigatória" : "";
    },
    redirectSupplier() {
        const theme = encodeURIComponent(JSON.stringify(this.whitelabel.data));
        this.svcV1
            .authenticate({
                usu_login: this.login,
                senha: this.password,
            })
            .then((resp) => {
                window.location.href =
                    CONSTANTS.API_REDIRECT_PROVIDER + "/login?external=1&token=" + resp.data.token + "&theme=" + theme;
            });
    },

    redirectClient() {
        const theme = encodeURIComponent(JSON.stringify(this.whitelabel.data));
        this.svcV1
            .authenticate({
                usu_login: this.login,
                senha: this.password,
            })
            .then((resp) => {
                window.location.href = "/cliente"+ "?token=" + resp.data.token + "&theme=" + theme;
            });
    },
    redirect(data) {
        if (data.password_reset && false) {
            this.storage_svc.put("reset-token", data.token);
            this.$router.push("/reset-password/");
        } else if (data.cli_id) {
            this.redirectClient();
        } else if (data.for_id) {
            this.redirectClient();
        } else {
            this.storage_svc.put("revenda", data.revenda_id);
            this.storage_svc.put("perfilId", data.perfil_id);
            this.storage_svc.put("token", data.token);
            this.storage_svc.put("user_name", data.name);
            this.storage_svc.put("perms", JSON.stringify(data.permissions));
            // Recursos do plano são do usuário: os do login anterior não valem.
            clear_features();

            this.$router.push("/cliente/monitores/cotacoes");
        }
    },
    clear_message() {
        this.msg_result = false;
        this.error_msg = "";
    },

    change_form() {
        this.clear_message();
        this.form_create = !this.form_create;
        this.password = "";
        this.email = "";
        this.login = "";
    },

    redirectToSupplier() {
        window.location.href = "https://novo.fornecedor.clubdacotacao.com.br/requisicao/fornecedor";
    },

    onPasswordInput(value) {
        this.password = value;
        this.validatePassword();
        this.validatePasswordMatch();
    },

    validatePassword() {
        const val = this.password;
        this.validations = {
            minLength: val.length >= 8,
            uppercase: /[A-Z]/.test(val),
            lowercase: /[a-z]/.test(val),
            number: /[0-9]/.test(val),
            special: /[^A-Za-z0-9]/.test(val),
        };
    },

    validatePasswordMatch() {
        if (this.confirmPassword && this.password !== this.confirmPassword) {
            this.fieldErrors.confirmPassword = "As senhas não coincidem";
        } else {
            this.clearFieldError("confirmPassword");
            this.clearError();
        }
    },

    validateForm() {
        let isValid = true;
        this.fieldErrors = {
            name: "",
            email: "",
            password: "",
            confirmPassword: "",
        };

        if (!this.name.trim()) {
            this.fieldErrors.name = "Nome é obrigatório";
            isValid = false;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!this.email.trim()) {
            this.fieldErrors.email = "Email é obrigatório";
            isValid = false;
        } else if (!emailRegex.test(this.email)) {
            this.fieldErrors.email = "Email inválido";
            isValid = false;
        }

        if (!this.password) {
            this.fieldErrors.password = "Senha é obrigatória";
            isValid = false;
        } else if (!this.isPasswordValid) {
            this.fieldErrors.password = "A senha não atende aos requisitos de segurança";
            isValid = false;
        }

        if (!this.confirmPassword) {
            this.fieldErrors.confirmPassword = "Confirmação de senha é obrigatória";
            isValid = false;
        } else if (this.password !== this.confirmPassword) {
            this.fieldErrors.confirmPassword = "As senhas não coincidem";
            isValid = false;
        }

        if (!this.acceptTerms) {
            this.error_msg = "Você deve aceitar os termos e condições para criar uma conta";
            isValid = false;
        }

        return isValid;
    },

    async efetuarCadastro() {
        if (!this.validateForm()) {
            return;
        }

        this.loading = true;
        this.error_msg = "";

        try {
            const response = await ClientServiceV3.createClient({
                name: this.name.trim(),
                email: this.email.trim().toLowerCase(),
                password: this.password,
            });

            if (response && response.data) {
                this.success_msg = "Conta criada com sucesso!";
                this.$router.push("/sign-in");
            }
        } catch (error) {
            console.error("Erro ao criar conta:", error);
            if (error.response && error.response.data) {
                const errorData = error.response.data;

                if (error.response.status === 409) {
                    this.error_msg = "Este email já está cadastrado";
                } else if (error.response.status === 400) {
                    this.error_msg = errorData.message || "Dados inválidos";
                } else {
                    this.error_msg = errorData.message || "Erro ao criar conta. Tente novamente.";
                }
            } else {
                this.error_msg = "Erro de conexão. Verifique sua internet e tente novamente.";
            }
        } finally {
            this.loading = false;
        }
    },
  },
  computed: {
    ...mapState(["whitelabel"]),
    isLogin() {
        return this.$route.path.includes("/login");
    },

    strengthLevel() {
        return Object.values(this.validations).filter(Boolean).length;
    },

    strengthText() {
        switch (this.strengthLevel) {
            case 0:
            case 1:
                return "Muito Fraca";
            case 2:
                return "Fraca";
            case 3:
            case 4:
                return "Boa";
            case 5:
                return "Forte";
            default:
                return "";
        }
    },

    strengthClass() {
        switch (this.strengthLevel) {
            case 0:
            case 1:
                return "very-weak";
            case 2:
                return "weak";
            case 3:
            case 4:
                return "good";
            case 5:
                return "strong";
            default:
                return "";
        }
    },

    isPasswordValid() {
        return Object.values(this.validations).every(Boolean);
    },

    ...mapState(["whitelabel"]),

    authHeaderStyle() {
        const imgs = this.whitelabel?.data?.imgs || {};

        const defaultLoginBg = require("@/assets/bg-signin.jpg");
        const defaultSignupBg = require("@/assets/signup-bg.jpg");

        const whiteLabelBg = imgs.img_background ?? imgs.img_bg_login ?? imgs.img_bg_signup ?? null;

        const bgImage = whiteLabelBg ? whiteLabelBg : this.isLogin ? defaultLoginBg : defaultSignupBg;

        return {
            backgroundImage: this.isLogin
                ? `url("${bgImage}")`
                : `
                linear-gradient(
                180deg,
                rgba(0, 0, 0, 0.4) 15%,
                rgba(255, 255, 255, 0) 49.35%,
                rgba(0, 0, 0, 0.4) 84.47%
                ),
                url("${bgImage}")
            `,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
        };
    },
    logoSrc() {
        const hasLogo = this.whitelabel && this.whitelabel.data && this.whitelabel.data.imgs && this.whitelabel.data.imgs.img_logo;
        return hasLogo ? this.whitelabel.data.imgs.img_logo : require("@/assets/logo-new.svg");
    },

    logoSrcMobile() {
        const hasLogo = this.whitelabel && this.whitelabel.data && this.whitelabel.data.imgs && this.whitelabel.data.imgs.img_logo;
        return hasLogo ? this.whitelabel.data.imgs.img_logo : require("@/assets/logo-mobile.svg");
    },

    logoAlt() {
        const hasLogo = this.whitelabel && this.whitelabel.data && this.whitelabel.data.imgs && this.whitelabel.data.imgs.img_logo;
        return hasLogo ? "Logo" : "Logo Clube da Cotação";
    },

    logoClass() {
        const hasLogo = this.whitelabel && this.whitelabel.data && this.whitelabel.data.imgs && this.whitelabel.data.imgs.img_logo;
        return hasLogo ? "img-logotipo-whitelabel" : "logo-auth";
    },

    logoClassMobile() {
        const hasLogo = this.whitelabel && this.whitelabel.data && this.whitelabel.data.imgs && this.whitelabel.data.imgs.img_logo;
        return hasLogo ? "img-logotipo-whitelabel-mobile" : "logo-mobile";
    },
  },
  watch: {
      isLogin: {
          immediate: true,
          handler(newVal, oldVal) {
              this.error_msg = "";
              this.fieldErrors = {
                  name: "",
                  password: "",
                  email: "",
                  confirmPassword: "",
                  login: "",
              };
          },
      },
  },
  created() {
      if (this.isLogin) {
          const savedLogin = localStorage.getItem("LoginInfo");
          if (savedLogin) {
              try {
                  const loginInfo = JSON.parse(savedLogin);
                  this.login = loginInfo.user || "";
                  this.password = loginInfo.password || "";
                  this.saveLogin = true;
              } catch (e) {
                  console.error("Erro ao carregar login salvo:", e);
              }
          }
      }
  },
};
</script>
<style lang="scss">
@import "./sigin.page";
</style>
